import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { GitMerge } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLeoMutations } from '../hooks';
import type { LeoEntry } from '../types';

interface LeoMergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The duplicate entry that will be archived. */
  source: LeoEntry | null;
  /** Candidate surviving entries (same type, active). */
  candidates: LeoEntry[];
}

/**
 * Case 360° (Lot 2) Stage 8 — curator merge: the source (duplicate) entry is
 * soft-deleted, the chosen target survives; the merge is logged.
 */
export function LeoMergeDialog({
  open,
  onOpenChange,
  source,
  candidates,
}: LeoMergeDialogProps) {
  const { t } = useTranslation();
  const { mergeMutation } = useLeoMutations();

  const [targetId, setTargetId] = React.useState<string>('');
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setTargetId('');
      setReason('');
    }
  }, [open]);

  const targets = candidates?.filter((c) => c?.id !== source?.id) ?? [];
  const canSubmit = !!source && !!targetId && !mergeMutation.isPending;

  const handleSubmit = async () => {
    if (!source || !canSubmit) return;
    await mergeMutation.mutateAsync({
      sourceId: source.id,
      targetId: Number(targetId),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4 text-slate-500" />
            {t('leo.mergeDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('leo.mergeDialogDescription', { name: source?.name ?? '' })}
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('leo.mergeTargetLabel')}
            </label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger>
                <SelectValue placeholder={t('leo.mergeTargetPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {targets.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name} — {c.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('leo.mergeReasonLabel')}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('leo.mergeReasonPlaceholder')}
              rows={2}
              maxLength={1000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mergeMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={!canSubmit}>
            {mergeMutation.isPending ? t('processing') : t('leo.mergeConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
